import React, { useCallback, useEffect } from 'react'

import { useStore } from '@tanstack/react-form'
import { HStack, SegmentedControl, Slider, Tooltip, VStack } from 'govwell-ui'
import { twMerge } from 'tailwind-merge'
import { LetterheadBackgroundColor } from 'types/graphql'

import { AcceptedImages } from 'src/constants'
import { PathPrefixes } from 'src/constants/file'
import { TailwindColor } from 'src/constants/theme/tailwind'
import { T } from 'src/govwell-ui'
import { useAppForm } from 'src/hooks/use-app-form'
import { getRequiredValidator } from 'src/utils/forms'
import {
  DEFAULT_LETTERHEAD_LOGO_SIZE_PX,
  getLetterheadBackgroundHex,
  LETTERHEAD_BACKGROUND_COLOR_LABELS,
  LETTERHEAD_BACKGROUND_COLORS,
} from 'src/utils/letterhead'

import { CenterType, LetterheadFormValues } from './types'

const colorOptions = LETTERHEAD_BACKGROUND_COLORS.map((value) => ({
  label: LETTERHEAD_BACKGROUND_COLOR_LABELS[value],
  value,
}))

type ColorSwatchProps = {
  color: LetterheadBackgroundColor
  hexColor: string
  isSelected: boolean
  onSelect: (color: LetterheadBackgroundColor) => void
  label: string
}

const ColorSwatch = React.memo(
  ({ color, hexColor, isSelected, onSelect, label }: ColorSwatchProps) => (
    <Tooltip content={label}>
      <button
        type="button"
        onClick={() => onSelect(color)}
        className={twMerge(
          'size-8 cursor-pointer rounded-md transition-all hover:scale-110',
          isSelected ? 'ring-2 ring-blue-500' : 'ring-0'
        )}
        style={{
          backgroundColor: hexColor,
          boxShadow: hexColor === TailwindColor.White ? 'inset 0 0 0 1px #d1d5db' : undefined,
        }}
        aria-label={label}
      />
    </Tooltip>
  )
)

type Props = {
  initialValues: LetterheadFormValues
  onFormValuesChange: (values: LetterheadFormValues) => void
}

const LetterheadForm = ({ initialValues, onFormValuesChange }: Props) => {
  const form = useAppForm({
    defaultValues: initialValues,
  })

  // Subscribe to form changes
  useStore(form.store)

  // Report form values changes to parent for autosave and preview
  useEffect(() => {
    onFormValuesChange(form.state.values)
  }, [form.state.values, onFormValuesChange])

  const centerType = form.getFieldValue('centerType')
  const centerFile = form.getFieldValue('centerFile')
  const backgroundColor = form.getFieldValue('backgroundColor')

  const handleCenterTypeChange = useCallback(
    (value: string) => {
      form.setFieldValue('centerType', value as CenterType)
    },
    [form]
  )

  const handleBackgroundColorChange = useCallback(
    (color: LetterheadBackgroundColor) => {
      form.setFieldValue('backgroundColor', color)
    },
    [form]
  )

  return (
    <VStack gap={4} align="stretch">
      <T.H3>Letterhead Properties</T.H3>

      <form.AppField name="name" validators={getRequiredValidator('Please enter a name')}>
        {(field) => <field.Input field={field} label="Name" isRequired />}
      </form.AppField>

      <form.AppField name="leftText" validators={getRequiredValidator('Please enter left text')}>
        {(field) => <field.TextArea field={field} label="Left Text" isRequired minRows={2} />}
      </form.AppField>

      <form.AppField name="rightText" validators={getRequiredValidator('Please enter right text')}>
        {(field) => <field.TextArea field={field} label="Right Text" isRequired minRows={2} />}
      </form.AppField>

      <SegmentedControl label="Center" value={centerType} onValueChange={handleCenterTypeChange}>
        <SegmentedControl.Item value={CenterType.TEXT}>Text</SegmentedControl.Item>
        <SegmentedControl.Item value={CenterType.FILE}>Logo</SegmentedControl.Item>
      </SegmentedControl>

      {centerType === CenterType.FILE ? (
        <>
          <form.AppField name="centerFile">
            {(field) => (
              <field.FileUpload
                field={field}
                label="Center File"
                accept={AcceptedImages}
                pathPrefix={PathPrefixes.Logos}
              />
            )}
          </form.AppField>

          {centerFile && (
            <form.AppField name="centerFileWidthPx">
              {(field) => (
                <Slider
                  label="Logo Size"
                  min={50}
                  max={200}
                  suffix="px"
                  value={field.state.value ?? DEFAULT_LETTERHEAD_LOGO_SIZE_PX}
                  onValueChange={field.setValue}
                />
              )}
            </form.AppField>
          )}
        </>
      ) : (
        <form.AppField
          name="centerText"
          validators={getRequiredValidator('Please enter center text')}
        >
          {(field) => <field.TextArea field={field} label="Center Text" isRequired minRows={2} />}
        </form.AppField>
      )}

      <VStack gap={2} align="stretch">
        <T.Label>Background Color</T.Label>
        <HStack gap={2} wrap="wrap">
          {colorOptions.map((option) => (
            <ColorSwatch
              key={option.value}
              color={option.value}
              hexColor={getLetterheadBackgroundHex(option.value)}
              isSelected={backgroundColor === option.value}
              onSelect={handleBackgroundColorChange}
              label={option.label}
            />
          ))}
        </HStack>
      </VStack>
    </VStack>
  )
}

export default LetterheadForm
